import { breakpoints, type Breakpoint } from '~/hooks/useTailwindBreakpoint'

export type Sizes = {
  [key in Breakpoint | 'default']?: `${number}vw` | `${number}px`
}

export const defaultImageFormat = 'webp'

export const parseSizes = (sizes: Sizes) => {
  return Object.entries(sizes)
    .sort(([keyA, _], [keyB, __]) => {
      if (keyA === 'default') return 1
      if (keyB === 'default') return -1

      const breakpointA = parseInt(breakpoints[keyA as Breakpoint])
      const breakpointB = parseInt(breakpoints[keyB as Breakpoint])

      return breakpointB - breakpointA
    })
    .map(([key, value]) => {
      if (key === 'default') {
        return value
      }
      const breakpointValue = breakpoints[key as Breakpoint]
      if (!breakpointValue) {
        return value // Fallback to the value if breakpoint not found
      }
      return `(min-width: ${breakpointValue}) ${value}`
    })
    .join(', ')
}

// ImageKit specific utilities
export const getImageKitUrl = (
  imagePath: string,
  options: {
    width?: number
    height?: number
    quality?: number
    format?: 'auto' | 'webp' | 'jpg' | 'jpeg' | 'png'
    transformation?: string
  } = {}
) => {
  // TODO: remove
  if (!imagePath) return ''

  if (imagePath.startsWith('http') || imagePath.startsWith('blob:')) {
    return imagePath // Return the full URL if already provided (including blob URLs)
  }

  const { width, height, quality, format, transformation } = options
  const baseUrl =
    typeof window !== 'undefined' && window.ENV?.IMAGEKIT_ENDPOINT
      ? window.ENV.IMAGEKIT_ENDPOINT
      : 'https://ik.imagekit.io/protocol'

  const cleanImagePath = imagePath.startsWith('/')
    ? imagePath.slice(1)
    : imagePath
  let url = `${baseUrl}/${cleanImagePath}`
  const params: string[] = []

  // If transformation string is provided, use it directly (it's already formatted)
  if (transformation) {
    // If width is also provided (from srcset), replace the width in transformation
    if (width) {
      // Parse the transformation string and replace width parameter
      const transformParts = transformation.split(',')
      const hasWidth = transformParts.some((part) => part.startsWith('w-'))

      if (hasWidth) {
        // Replace existing width
        const newTransform = transformParts
          .map((part) => (part.startsWith('w-') ? `w-${width}` : part))
          .join(',')
        params.push(newTransform)
      } else {
        // Add width to transformation
        params.push(`w-${width}`, transformation)
      }
    } else {
      params.push(transformation)
    }
  } else {
    // Legacy individual parameters
    if (width) params.push(`w-${width}`)
    if (height) params.push(`h-${height}`)
    if (quality) params.push(`q-${quality}`)
    if (format) params.push(`f-${format}`)
  }

  if (params.length > 0) {
    url += `?tr=${params.join(',')}`
  }

  return url
}

export const getImageAttributes = (
  imagePath: string,
  options: {
    width?: number
    height?: number
    quality?: number
    format?: 'auto' | 'webp' | 'jpg' | 'jpeg' | 'png'
    transformation?: string
    alt?: string
  } = {}
) => {
  const { alt = '', ...urlOptions } = options
  const src = getImageKitUrl(imagePath, urlOptions)

  return {
    src,
    alt,
    ...urlOptions,
  }
}

export type TransformationOptions = {
  width?: number
  height?: number
  quality?: number
  format?: 'auto' | 'webp' | 'jpg' | 'jpeg' | 'png'
  dpr?: number
  /**
   * Resize mode for image transformation:
   * - 'fit': Resize to fit within dimensions without cropping (like object-fit: contain)
   * - 'fill': Resize to fill dimensions while maintaining aspect ratio (like object-fit: cover) - DEFAULT
   * - 'crop': Resize and crop to exact dimensions (forces exact size)
   * - 'scale': Resize to specified dimensions, may crop to fit exact size
   */
  resizeMode?: 'fit' | 'fill' | 'crop' | 'scale'
}

// Helper function to convert transformation object to ImageKit string format
export const convertTransformationToString = (
  transformation: TransformationOptions
): string => {
  const params: string[] = []

  // Add width and height based on resize mode
  if (transformation.width || transformation.height) {
    if (transformation.width) params.push(`w-${transformation.width}`)
    if (transformation.height) params.push(`h-${transformation.height}`)

    switch (transformation.resizeMode) {
      case 'fit':
        // Fit mode: resize to fit within dimensions without cropping (like object-fit: contain)
        params.push('c-at_max') // Don't crop, fit within bounds
        break

      case 'crop':
        // Crop mode: resize and crop to exact dimensions (forces exact size)
        params.push('c-maintain_ratio') // Maintain aspect ratio and crop
        break

      case 'scale':
        // Scale mode: resize to specified dimensions, may crop to fit exact size
        break

      default:
        // Fill/Cover mode: resize to fill dimensions while maintaining aspect ratio (like object-fit: cover)
        params.push('c-at_least') // Fill the dimensions, may crop
        break
    }
  }

  // Add other transformation parameters
  if (transformation.quality) params.push(`q-${transformation.quality}`)
  if (transformation.format) params.push(`f-${transformation.format}`)
  if (transformation.dpr) params.push(`dpr-${transformation.dpr}`)

  return params.join(',')
}

// Helper function to calculate image width from viewport width
const calculateImageWidth = (
  viewportWidth: string,
  breakpoint?: string
): number => {
  const defaultWidth = 1920

  // Generate breakpoint values from existing breakpoints
  const breakpointValues = Object.fromEntries(
    Object.entries(breakpoints).map(([key, value]) => [
      key,
      parseInt(value.replace('px', '')),
    ])
  ) as Record<string, number>

  if (viewportWidth.endsWith('vw')) {
    if (breakpoint && breakpoint !== 'default') {
      // For breakpoint-specific calculations (e.g., sm: '50vw' on 640px screen)
      const breakpointWidth = breakpointValues[breakpoint]

      if (breakpointWidth) {
        const vwPercentage = parseInt(viewportWidth.replace('vw', ''))
        return Math.round((vwPercentage / 100) * breakpointWidth)
      }
    }

    // For default vw (e.g., default: '80vw'), calculate based on defaultWidth
    const vwPercentage = parseInt(viewportWidth.replace('vw', ''))
    return Math.round((vwPercentage / 100) * defaultWidth)
  }

  if (viewportWidth.endsWith('px')) {
    // For fixed pixel widths (e.g., default: '400px')
    return parseInt(viewportWidth.replace('px', ''))
  }

  return defaultWidth
}

export const buildSrcSet = (
  src: string,
  sizes: Sizes | undefined,
  options: {
    height?: number
    transformation?: string
  } = {}
) => {
  const imageWidths: number[] = []

  if (sizes) {
    // Calculate image widths for each breakpoint in the sizes object
    Object.entries(sizes).forEach(([breakpoint, viewportWidth]) => {
      const width = calculateImageWidth(viewportWidth, breakpoint)
      if (width > 0) {
        imageWidths.push(width)
        imageWidths.push(width * 2) // 2x for retina
      }
    })
  } else {
    // When no sizes are provided, generate srcset based on breakpoints up to 1920px
    const breakpointValues = Object.fromEntries(
      Object.entries(breakpoints).map(([key, value]) => [
        key,
        parseInt(value.replace('px', '')),
      ])
    ) as Record<string, number>

    Object.values(breakpointValues).forEach((breakpointWidth) => {
      imageWidths.push(breakpointWidth)
    })

    // Add 1920px as the maximum
    imageWidths.push(1920)
  }

  // Remove duplicates and sort
  const uniqueWidths = [...new Set(imageWidths)].sort((a, b) => a - b)

  if (uniqueWidths.length === 0) {
    return undefined
  }

  return uniqueWidths
    .map((width) => {
      const imageUrl = getImageAttributes(src, {
        width,
        height: options.height,
        transformation: options.transformation,
      }).src
      return `${imageUrl} ${width}w`
    })
    .join(', ')
}

const hexToRgb = (hex: string) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  if (!result) {
    return { r: 0, g: 0, b: 0 }
  }
  return {
    r: parseInt(result[1] ?? '0', 16),
    g: parseInt(result[2] ?? '0', 16),
    b: parseInt(result[3] ?? '0', 16),
  }
}

// Cache for generated base64 images to avoid regenerating the same colors
const base64ImageCache = new Map<string, string>()

/**
 * Generates a base64 encoded PNG image of a colored square using Sharp
 * @param color - Hex color code (e.g., '#FF0000')
 * @param size - Image dimensions (width = height for square)
 * @param borderRadius - Border radius in pixels (0 = no rounding, size/2 = fully rounded)
 * @returns Base64 encoded PNG data URL
 */
export const generateColoredImageBase64 = async (
  color: string,
  size: number = 192
): Promise<string> => {
  const cacheKey = `${color}-${size}`
  const borderRadius = 4

  if (base64ImageCache.has(cacheKey)) {
    return base64ImageCache.get(cacheKey)!
  }

  try {
    const sharp = await import('sharp')

    const { r, g, b } = hexToRgb(color)

    // Create the base colored image with rounded corners using SVG
    const imageBuffer = await sharp
      .default({
        create: {
          width: size,
          height: size,
          channels: 4,
          background: { r, g, b, alpha: 1 },
        },
      })
      .composite([
        {
          input: Buffer.from(
            `<svg width="${size}" height="${size}">
                <rect width="${size}" height="${size}" rx="${borderRadius}" ry="${borderRadius}" fill="white"/>
              </svg>`
          ),
          blend: 'dest-in',
        },
      ])
      .png()
      .toBuffer()

    // Convert to base64
    const base64String = `data:image/png;base64,${imageBuffer.toString('base64')}`

    // Cache the result
    base64ImageCache.set(cacheKey, base64String)

    return base64String
  } catch (error) {
    console.error('Error generating colored image with Sharp:', error)
    return ''
  }
}

/**
 * Generates a base64 encoded PNG image of a colored square using Sharp (without mask)
 * @param color - Hex color code (e.g., '#FF0000')
 * @param size - Image dimensions (width = height for square)
 * @returns Base64 encoded PNG data URL
 */
export const generateColoredImageBase64WithoutMask = async (
  color: string,
  size: number = 192
): Promise<string> => {
  const cacheKey = `no-mask-${color}-${size}`

  if (base64ImageCache.has(cacheKey)) {
    return base64ImageCache.get(cacheKey)!
  }

  try {
    const sharp = await import('sharp')

    const { r, g, b } = hexToRgb(color)

    // Create the base colored image without mask
    const imageBuffer = await sharp
      .default({
        create: {
          width: size,
          height: size,
          channels: 4,
          background: { r, g, b, alpha: 1 },
        },
      })
      .png()
      .toBuffer()

    // Convert to base64
    const base64String = `data:image/png;base64,${imageBuffer.toString('base64')}`

    // Cache the result
    base64ImageCache.set(cacheKey, base64String)

    return base64String
  } catch (error) {
    console.error('Error generating colored image without mask:', error)
    return ''
  }
}

/**
 * Creates a simple ICO file structure from PNG buffer
 * Based on Microsoft ICO specification: https://docs.microsoft.com/en-us/windows/win32/uxguide/vis-icons
 * @param pngBuffer - PNG image data as Buffer
 * @returns ICO file as Buffer
 */
function createSimpleICO(pngBuffer: Buffer): Buffer {
  // ICO file structure:
  // - 6-byte header
  // - Image directory entry (16 bytes)
  // - PNG data

  const header = Buffer.alloc(6)
  header.writeUInt16LE(0, 0) // Reserved (must be 0)
  header.writeUInt16LE(1, 2) // Type (1 = ICO, 2 = CUR)
  header.writeUInt16LE(1, 4) // Number of images

  const directory = Buffer.alloc(16)
  directory.writeUInt8(32, 0) // Width (0-255, 0 = 256px)
  directory.writeUInt8(32, 1) // Height (0-255, 0 = 256px)
  directory.writeUInt8(0, 2) // Color palette (0 = no palette)
  directory.writeUInt8(0, 3) // Reserved
  directory.writeUInt16LE(1, 4) // Color planes (usually 1)
  directory.writeUInt16LE(32, 6) // Bits per pixel
  directory.writeUInt32LE(pngBuffer.length, 8) // Size of image data
  directory.writeUInt32LE(22, 12) // Offset to image data (6 + 16)

  return Buffer.concat([header, directory, pngBuffer])
}

/**
 * Generates a base64 encoded PNG image for shop logos using theme color (Server-side)
 * @param themeColor - Shop theme color (hex)
 * @param size - Logo dimensions (default: 192x192)
 * @returns Base64 encoded PNG data URL of a colored square
 */
export const generateShopLogoBase64 = async (
  themeColor?: string | null,
  size: number = 192
): Promise<string> => {
  // Handle empty or null theme color
  if (!themeColor) {
    return ''
  }

  const isValidHexColor = /^#[0-9A-F]{6}$/i.test(themeColor)

  if (!isValidHexColor) {
    console.warn(
      `Invalid theme color format: ${themeColor}. Returning empty string.`
    )
    return ''
  }

  return generateColoredImageBase64(themeColor, size)
}

/**
 * Generates both PNG and ICO versions of shop logo
 * @param themeColor - Shop's theme color
 * @returns Object with PNG and ICO base64 data URLs
 */
export const generateShopLogoFormats = async (
  themeColor?: string | null
): Promise<{ png: string; ico: string }> => {
  if (!themeColor) {
    return { png: '', ico: '' }
  }

  const isValidHexColor = /^#[0-9A-F]{6}$/i.test(themeColor)

  if (!isValidHexColor) {
    console.warn(
      `Invalid theme color format: ${themeColor}. Returning empty strings.`
    )
    return { png: '', ico: '' }
  }

  try {
    // Generate PNG (32x32)
    const pngBase64 = await generateColoredImageBase64(themeColor, 32)
    if (!pngBase64?.includes(',')) {
      return { png: '', ico: '' }
    }
    const base64Data = pngBase64.split(',')[1]
    if (!base64Data) {
      return { png: '', ico: '' }
    }
    const pngBuffer = Buffer.from(base64Data, 'base64')

    // Create ICO from PNG
    const icoBuffer = createSimpleICO(pngBuffer)
    const icoBase64 = `data:image/x-icon;base64,${icoBuffer.toString('base64')}`

    // Generate larger PNG for other uses (with fixed mask)
    const pngBase64Large = await generateColoredImageBase64(themeColor, 192)

    return {
      png: pngBase64Large,
      ico: icoBase64,
    }
  } catch (error) {
    console.error('Error generating shop logo formats:', error)
    return { png: '', ico: '' }
  }
}
